import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import {
  CompanyAuthError,
  CompanyJwt,
  loginViaCompany,
  logoutCompany,
  refreshJwt,
} from '@/lib/companyAuth';
import {
  getJwt,
  setJwt as setHolderJwt,
  setRefresher,
  setSessionLostHandler,
  setUnclaimedHandler,
} from '@/lib/jwtHolder';
import { User } from '@/types';

// ---------------------------------------------------------------------------
// Public context shape
// ---------------------------------------------------------------------------
interface AuthContextType {
  user: User | null;
  /** Company-issued JWT for outgoing /api/* calls. Use fetchWithAuth instead of reading this directly. */
  token: string | null;
  /** Short-lived HS256 token for Socket.IO. */
  socketToken: string | null;
  loading: boolean;
  /** Submit credentials through the SSO widget. Cross-origin to Company. */
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Re-fetch /api/auth/session — call after profile edits, claim, etc. */
  refreshUser: () => Promise<void>;
  /** Manual JWT refresh; usually triggered automatically. */
  refreshToken: () => Promise<boolean>;
  /** Show / hide the global login modal. */
  showLoginModal: boolean;
  setShowLoginModal: (show: boolean) => void;
  /** Show the claim-username modal for `companyUserId` (set by 409 handler). */
  unclaimedCompanyUserId: string | null;
  /** Submit a desired username; server returns suggestions on collision. */
  claimUsername: (username: string) => Promise<
    { ok: true; user: User } | { ok: false; error: string; suggested?: string[] }
  >;
  /** Opt out of creating an EarthSocial profile (this browser session only).
   *  The user keeps their Company account; they just don't have an
   *  EarthSocial row. Signing in again clears the flag. */
  declineClaim: () => void;
  requireAuth: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
const REFRESH_LEAD_MS = 30 * 1000; // refresh 30 s before expiry
const REFRESH_TICK_MS = 60 * 1000; // poll for expiry every minute
const DECLINE_FLAG = 'earthsocial:declined-claim'; // sessionStorage: skip silent refresh this session

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [socketToken, setSocketToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [unclaimedCompanyUserId, setUnclaimedCompanyUserId] = useState<string | null>(null);

  const expiresAtRef = useRef<number | null>(null); // ms since epoch
  const mountedRef = useRef(true);

  // Update both React state and the module-level holder so fetchWithAuth sees fresh JWTs.
  const applyJwt = useCallback((cj: CompanyJwt | null) => {
    if (cj) {
      setToken(cj.jwt);
      setHolderJwt(cj.jwt);
      expiresAtRef.current = new Date(cj.expiresAt).getTime();
    } else {
      setToken(null);
      setHolderJwt(null);
      expiresAtRef.current = null;
    }
  }, []);

  /** Fetch /api/auth/session and apply the user + socket token. Returns:
   *   - 'ok'        — set user, set socket token
   *   - 'unclaimed' — server returned 409, AuthContext will trigger the claim modal
   *   - 'lost'      — server returned 401 or other auth failure
   */
  const fetchSession = useCallback(async (): Promise<'ok' | 'unclaimed' | 'lost'> => {
    const jwt = getJwt();
    if (!jwt) return 'lost';
    const res = await fetch('/api/auth/session', {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    if (res.status === 409) {
      const body = await res.json().catch(() => ({}));
      if (typeof body?.companyUserId === 'string') {
        setUnclaimedCompanyUserId(body.companyUserId);
      }
      return 'unclaimed';
    }
    if (!res.ok) return 'lost';
    const data = await res.json();
    if (mountedRef.current) {
      setUser(data.user as User);
      setSocketToken(data.socketToken ?? null);
    }
    return 'ok';
  }, []);

  // Silent refresh: hit Company /auth/external-token (cookie auth). Returns true on success.
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const cj = await refreshJwt();
      if (!cj) {
        applyJwt(null);
        return false;
      }
      applyJwt(cj);
      return true;
    } catch (err) {
      console.warn('[Auth] silent refresh failed:', err);
      applyJwt(null);
      return false;
    }
  }, [applyJwt]);

  // Wire the refresher + handlers into the module-level holder so fetchWithAuth can use them.
  useEffect(() => {
    setRefresher(async () => {
      const ok = await refreshToken();
      return ok ? getJwt() : null;
    });
    setUnclaimedHandler((id) => {
      if (mountedRef.current) setUnclaimedCompanyUserId(id);
    });
    setSessionLostHandler(() => {
      if (mountedRef.current) {
        setUser(null);
        applyJwt(null);
        setSocketToken(null);
        setShowLoginModal(true);
      }
    });
  }, [refreshToken, applyJwt]);

  // Bootstrap on mount: try silent refresh; if successful, fetch session.
  // If the user previously declined to create an EarthSocial profile this
  // browser session, skip the refresh entirely so they're not re-prompted.
  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(DECLINE_FLAG) === '1') {
        if (mountedRef.current) setLoading(false);
        return;
      }
      const refreshed = await refreshToken();
      if (refreshed) {
        await fetchSession();
      }
      if (mountedRef.current) setLoading(false);
    })();
    return () => {
      mountedRef.current = false;
    };
  }, [refreshToken, fetchSession]);

  // Proactive refresh tick: every 60 s, refresh if within 30 s of expiry.
  useEffect(() => {
    if (!token) return;
    const id = window.setInterval(() => {
      const exp = expiresAtRef.current;
      if (!exp) return;
      if (exp - Date.now() < REFRESH_LEAD_MS) {
        refreshToken();
      }
    }, REFRESH_TICK_MS);
    return () => window.clearInterval(id);
  }, [token, refreshToken]);

  // Show login modal whenever the user lands on a protected route without auth.
  // Public read-only pages (/terms, /privacy) and the auth surfaces (/login,
  // /register, /post-signup) must not auto-pop the modal — those pages are
  // often opened in a new tab by signed-out or partially-signed-in users
  // (e.g. from inside the claim-username flow), and a popping modal would
  // hide the content they came to read.
  useEffect(() => {
    if (loading) return;
    if (user) return;
    const path = window.location.pathname;
    const skip = ['/login', '/register', '/post-signup', '/terms', '/privacy'];
    if (skip.includes(path)) return;
    setShowLoginModal(true);
  }, [loading, user]);

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      setLoading(true);
      // Explicit sign-in clears any "I don't want EarthSocial" flag.
      sessionStorage.removeItem(DECLINE_FLAG);
      try {
        const cj = await loginViaCompany(email, password);
        applyJwt(cj);
        const outcome = await fetchSession();
        if (outcome === 'unclaimed') {
          // The claim modal will appear via state; success path runs after claim.
        }
        if (outcome === 'ok' && mountedRef.current) {
          setShowLoginModal(false);
        }
      } catch (err) {
        if (err instanceof CompanyAuthError) throw err;
        throw new CompanyAuthError('login_failed', (err as Error).message || 'Login failed');
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [applyJwt, fetchSession],
  );

  const logout = useCallback(async (): Promise<void> => {
    await logoutCompany();
    if (!mountedRef.current) return;
    setUser(null);
    applyJwt(null);
    setSocketToken(null);
    setShowLoginModal(true);
    window.dispatchEvent(new Event('auth-change'));
  }, [applyJwt]);

  const refreshUser = useCallback(async (): Promise<void> => {
    await fetchSession();
  }, [fetchSession]);

  const claimUsername = useCallback(
    async (
      username: string,
    ): Promise<
      { ok: true; user: User } | { ok: false; error: string; suggested?: string[] }
    > => {
      const jwt = getJwt();
      if (!jwt) return { ok: false, error: 'no_session' };
      const res = await fetch('/api/auth/claim-username', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ username }),
      });
      if (res.status === 201) {
        const body = await res.json();
        setUnclaimedCompanyUserId(null);
        await fetchSession();
        return { ok: true, user: body.user as User };
      }
      const body = await res.json().catch(() => ({}));
      return {
        ok: false,
        error: body?.error ?? 'claim_failed',
        suggested: Array.isArray(body?.suggested) ? body.suggested : undefined,
      };
    },
    [fetchSession],
  );

  const declineClaim = useCallback((): void => {
    sessionStorage.setItem(DECLINE_FLAG, '1');
    applyJwt(null);
    setUnclaimedCompanyUserId(null);
    setUser(null);
    setSocketToken(null);
    // Don't pop the login modal — they're choosing to browse without an
    // EarthSocial profile. They can sign in from /login if they change their mind.
  }, [applyJwt]);

  const requireAuth = useCallback((): boolean => {
    if (user) return true;
    setShowLoginModal(true);
    return false;
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      token,
      socketToken,
      loading,
      login,
      logout,
      refreshUser,
      refreshToken,
      showLoginModal,
      setShowLoginModal,
      unclaimedCompanyUserId,
      claimUsername,
      declineClaim,
      requireAuth,
    }),
    [
      user,
      token,
      socketToken,
      loading,
      login,
      logout,
      refreshUser,
      refreshToken,
      showLoginModal,
      unclaimedCompanyUserId,
      claimUsername,
      declineClaim,
      requireAuth,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
